import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSave,
  faSpinner,
  faCheckCircle,
  faBookOpen,
  faGlobe,
  faFileContract,
  faCogs,
  faBullseye,
  faPalette,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import axios from "../../axios";
import { IService } from "../../interfaces/domain";
import {
  IServiceKnowledgeBase,
  getKnowledgeBase,
  updateKnowledgeBase,
  listKnowledgeBases,
  createKnowledgeBase,
} from "../../services/OnboardingService";
import classes from "./Onboarding.module.scss";

interface IKnowledgeBaseTabProps {
  initialServiceToken?: string;
  onClearInitialToken?: () => void;
}

export const KnowledgeBaseTab: React.FC<IKnowledgeBaseTabProps> = ({
  initialServiceToken,
  onClearInitialToken,
}) => {
  const [serviceToken, setServiceToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Active KB entity
  const [kb, setKb] = useState<IServiceKnowledgeBase | null>(null);

  // Active sub-tab inside KB Editor
  const [activeSection, setActiveSection] = useState<
    "general" | "service" | "evaluation" | "compliance" | "marketing" | "branding"
  >("general");

  const [kbList, setKbList] = useState<IServiceKnowledgeBase[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [services, setServices] = useState<IService[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Creation modal/view state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<"linked" | "independent">("linked");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [newServiceToken, setNewServiceToken] = useState("");
  const [newVerticalName, setNewVerticalName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleFetchKbList = async () => {
    setLoadingList(true);
    try {
      const list = await listKnowledgeBases();
      setKbList(list);
    } catch (err) {
      console.error("Error fetching KBs:", err);
      setError("Virhe haettaessa tietoalustoja.");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await axios.post('/json/services/list', {});
      setServices(res.data || []);
    } catch (err) {
      console.error("Error fetching services:", err);
    }
  };

  useEffect(() => {
    handleFetchKbList();
    fetchServices();
  }, []);

  const handleCreateKB = async () => {
    if (!newServiceToken.trim() || !newVerticalName.trim()) {
      setCreateError("Kaikki tähdellä merkityt kentät ovat pakollisia.");
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      const payload = {
        serviceToken: newServiceToken.trim().toLowerCase(),
        verticalName: newVerticalName.trim(),
        serviceId: createType === "linked" ? selectedServiceId : null,
      };

      const newKb = await createKnowledgeBase(payload);
      setKb(newKb);
      setServiceToken(newKb.serviceToken);
      setShowCreateModal(false);
      setSuccess("Uusi tietoalusta luotu onnistuneesti!");
      
      // Refresh list
      handleFetchKbList();
    } catch (err: any) {
      console.error("Error creating KB:", err);
      setCreateError(err.response?.data || "Virhe luotaessa tietoalustaa. Tunnus saattaa olla jo käytössä.");
    } finally {
      setCreateLoading(false);
    }
  };

  // Load knowledge base
  const handleLoadKB = useCallback(async (tokenToLoad?: string) => {
    const token = tokenToLoad || serviceToken;
    if (!token.trim()) {
      setError("Palvelutunniste (Service Token) on pakollinen.");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await getKnowledgeBase(token.trim().toLowerCase());
      setKb(data);
      setServiceToken(token.trim().toLowerCase());
    } catch (err) {
      console.error("Error loading KB:", err);
      setError("Kyseiselle palvelutunnisteelle ei löytynyt aiempaa Knowledge Basea tai tapahtui virhe.");
    } finally {
      setLoading(false);
    }
  }, [serviceToken]);

  useEffect(() => {
    if (initialServiceToken) {
      setServiceToken(initialServiceToken);
      handleLoadKB(initialServiceToken);
      if (onClearInitialToken) {
        onClearInitialToken();
      }
    }
  }, [initialServiceToken, onClearInitialToken, handleLoadKB]);

  // Helper to validate and format JSON strings
  const validateJson = (str?: string | null): boolean => {
    if (!str || !str.trim()) return true;
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Save changes
  const handleSaveKB = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kb) return;

    setError(null);
    setSuccess(null);

    // Validate all JSON fields
    const jsonFields: Array<{ name: string; val: string | null | undefined }> = [
      { name: "TOL Codes JSON", val: kb.tolCodesJson },
      { name: "TOL Code Names JSON", val: kb.tolCodeNamesJson },
      { name: "Google Places Queries JSON", val: kb.googlePlacesQueriesJson },
      { name: "Required Certifications JSON", val: kb.requiredCertificationsJson },
      { name: "Certification Bodies JSON", val: kb.certificationBodiesJson },
      { name: "Exclude Keywords JSON", val: kb.excludeKeywordsJson },
      { name: "Region Limitations JSON", val: kb.regionLimitationsJson },
      { name: "Typical Includes JSON", val: kb.typicalIncludesJson },
      { name: "Typical Excludes JSON", val: kb.typicalExcludesJson },
      { name: "Required Permits JSON", val: kb.requiredPermitsJson },
      { name: "Regulatory Bodies JSON", val: kb.regulatoryBodiesJson },
      { name: "Compliance Source URLs JSON", val: kb.complianceSourceUrlsJson },
      { name: "Target Groups JSON", val: kb.targetGroupsJson },
      { name: "Primary Triggers JSON", val: kb.primaryTriggersJson },
      { name: "Customer Fears JSON", val: kb.customerFearsJson },
      { name: "FAQs JSON", val: kb.faqsJson },
      { name: "Secondary Keywords JSON", val: kb.secondaryKeywordsJson },
      { name: "Keyword Clusters JSON", val: kb.keywordClustersJson },
      { name: "Generated Articles JSON", val: kb.generatedArticlesJson },
      { name: "Info Grid Blocks JSON", val: kb.infoGridBlocksJson },
    ];

    for (const f of jsonFields) {
      if (f.val && !validateJson(f.val)) {
        setError(`Virheellinen JSON-formaatti kentässä '${f.name}'. Varmista, että se on oikea JSON-lista tai -objekti (esim. ["A", "B"]).`);
        return;
      }
    }

    setSaving(true);
    try {
      const updated = await updateKnowledgeBase(kb);
      setKb(updated);
      setSuccess("Knowledge Base tallennettu onnistuneesti!");
    } catch (err) {
      console.error("Error saving KB:", err);
      setError("Virhe tallennettaessa Knowledge Basea.");
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (key: keyof IServiceKnowledgeBase, value: any) => {
    if (!kb) return;
    setKb({
      ...kb,
      [key]: value === "" ? null : value,
    });
  };

  // Pre-load common verticals for quick click
  const quickVerticals = ["latauspiste", "ilmalampopumppu", "aurinkosahko", "remontti", "siivous"];

  // Filter list based on search term
  const filteredKbList = kbList.filter((k: IServiceKnowledgeBase) => 
    (k.verticalName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (k.serviceToken || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={classes.KbTabContainer}>
      {/* HEADER BLOCK */}
      <div className={classes.TabHeader}>
        <div className={classes.TabTitleBlock}>
          <h2>Palvelukohtaiset Tietoalustat (Knowledge Bases)</h2>
          <p>
            Konfiguroi palvelukohtaisia arviointikriteerejä, hakuavaimia, toimialarajoja, lainsäädäntöä ja markkinointitietoa. AI-agentit injektoivat nämä tiedot hakuprosesseissa, evaluoinneissa ja tarjouskirjeluonnoksissa.
          </p>
        </div>
        {kb && (
          <div>
            <button 
              className={classes.SecondaryBtn} 
              onClick={() => {
                setKb(null);
                setServiceToken("");
                setSuccess(null);
                setError(null);
                handleFetchKbList();
              }}
            >
              Sulje tietoalusta & Palaa listaan
            </button>
          </div>
        )}
      </div>

      {/* QUICK VERTICAL SELECTOR (ONLY SHOWN IF EDITOR OPEN) */}
      {kb && (
        <div className={classes.KbLoaderSection}>
          <div className={classes.SearchBoxGroup}>
            <input
              type="text"
              className={classes.KbSearchInput}
              placeholder="Hae tai luo syöttämällä palvelutunniste (esim. latauspiste)"
              value={serviceToken}
              onChange={(e) => setServiceToken(e.target.value)}
            />
            <button
              className={classes.PrimaryBtn}
              onClick={() => handleLoadKB()}
              disabled={loading || !serviceToken.trim()}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} /> Lataa tietoalusta
                </>
              )}
            </button>
          </div>

          <div className={classes.QuickSelectGroup}>
            <span>Pikavalinnat:</span>
            {quickVerticals.map((v) => (
              <button
                key={v}
                className={`${classes.QuickSelectBtn} ${serviceToken === v ? classes.QuickActive : ""}`}
                onClick={() => {
                  setServiceToken(v);
                  handleLoadKB(v);
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ERROR & SUCCESS MESSAGES */}
      {error && <div className={classes.ErrorMsg}>{error}</div>}
      {success && (
        <div className={classes.SuccessMsg}>
          <FontAwesomeIcon icon={faCheckCircle} /> {success}
        </div>
      )}

      {/* EDITOR WORKSPACE OR LIST VIEW */}
      {kb ? (
        <div className={classes.KbWorkspace}>
          {/* Sub-navigation bar inside KB */}
          <div className={classes.KbSubNav}>
            <button
              className={`${classes.SubNavBtn} ${activeSection === "general" ? classes.SubNavBtnActive : ""}`}
              onClick={() => setActiveSection("general")}
            >
              <FontAwesomeIcon icon={faBookOpen} /> 1. Perustiedot & Toimialat
            </button>
            <button
              className={`${classes.SubNavBtn} ${activeSection === "service" ? classes.SubNavBtnActive : ""}`}
              onClick={() => setActiveSection("service")}
            >
              <FontAwesomeIcon icon={faGlobe} /> 2. Palvelun Sisältö
            </button>
            <button
              className={`${classes.SubNavBtn} ${activeSection === "evaluation" ? classes.SubNavBtnActive : ""}`}
              onClick={() => setActiveSection("evaluation")}
            >
              <FontAwesomeIcon icon={faCogs} /> 3. AI Evaluointi & Sertifikaatit
            </button>
            <button
              className={`${classes.SubNavBtn} ${activeSection === "compliance" ? classes.SubNavBtnActive : ""}`}
              onClick={() => setActiveSection("compliance")}
            >
              <FontAwesomeIcon icon={faFileContract} /> 4. Sääntely & Verotus
            </button>
            <button
              className={`${classes.SubNavBtn} ${activeSection === "marketing" ? classes.SubNavBtnActive : ""}`}
              onClick={() => setActiveSection("marketing")}
            >
              <FontAwesomeIcon icon={faBullseye} /> 5. Kohderyhmät & Asiakkaat
            </button>
            <button
              className={`${classes.SubNavBtn} ${activeSection === "branding" ? classes.SubNavBtnActive : ""}`}
              onClick={() => setActiveSection("branding")}
            >
              <FontAwesomeIcon icon={faPalette} /> 6. SEO & Brändi-ilme
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSaveKB} className={classes.KbForm}>
            <div className={classes.FormSectionContent}>
              
              {/* SECTION 1: GENERAL */}
              {activeSection === "general" && (
                <div className={classes.FormTabPane}>
                  <h3>Palveluvertikaalin perustiedot ja rajaukset</h3>
                  <div className={classes.FormGrid}>
                    <div className={classes.FormGroup}>
                      <label>Vertical Name (fi) <span className={classes.Required}>*</span></label>
                      <input
                        type="text"
                        placeholder="Esim. Sähköautojen latauspisteet"
                        value={kb.verticalName || ""}
                        onChange={(e) => handleFieldChange("verticalName", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Palvelutunniste (Service Token) <span className={classes.Required}>*</span></label>
                      <input
                        type="text"
                        placeholder="Esim. latauspiste"
                        value={kb.serviceToken || ""}
                        onChange={(e) => handleFieldChange("serviceToken", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                      />
                      <span className={classes.FormHelper}>Ainutlaatuinen tunniste (slug).</span>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label style={{ fontWeight: 700, color: "#0f172a" }}>Kytkentä olemassa olevaan palveluun (Service.java)</label>
                      <div style={{ display: "flex", gap: "16px", alignItems: "center", marginTop: "4px" }}>
                        <select
                          value={kb.serviceId || ""}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            if (selectedId === "") {
                              setKb({
                                ...kb,
                                serviceId: null,
                              });
                            } else {
                              const foundSvc = services.find(s => String(s.id) === String(selectedId));
                              if (foundSvc) {
                                setKb({
                                  ...kb,
                                  serviceId: foundSvc.id,
                                  serviceToken: foundSvc.serviceToken || kb.serviceToken,
                                  verticalName: kb.verticalName || foundSvc.name || null,
                                });
                              }
                            }
                          }}
                          style={{
                            padding: "10px 12px",
                            border: "1px solid #cbd5e1",
                            borderRadius: "6px",
                            fontSize: "0.88rem",
                            backgroundColor: "white",
                            outline: "none",
                            minWidth: "250px",
                          }}
                        >
                          <option value="">-- Itsenäinen (ei kytkettyä palvelua) --</option>
                          {services.map(s => (
                            <option key={s.id} value={s.id}>{s.name} ({s.serviceToken})</option>
                          ))}
                        </select>
                        <span style={{ fontSize: "0.82rem", color: kb.serviceId ? "#16a34a" : "#64748b", fontWeight: kb.serviceId ? 600 : 400 }}>
                          {kb.serviceId ? (
                            "✓ Kytketty palveluun! Tietoalusta käyttää tämän palvelun taustatietoja."
                          ) : (
                            "⚠ Itsenäinen tietoalusta. Voit kytkeä tämän olemassa olevaan palveluun valitsemalla sen listasta."
                          )}
                        </span>
                      </div>
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Primary Keyword</label>
                      <input
                        type="text"
                        placeholder="Esim. latauspistokkeen asennus"
                        value={kb.primaryKeyword || ""}
                        onChange={(e) => handleFieldChange("primaryKeyword", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Google Places Hakulausekkeet (JSON-lista)</label>
                      <textarea
                        placeholder='["sähköauton latauspiste asennus", "sähköasennus", "latausasema kotiin"]'
                        value={kb.googlePlacesQueriesJson || ""}
                        onChange={(e) => handleFieldChange("googlePlacesQueriesJson", e.target.value)}
                        rows={3}
                        className={classes.CodeTextArea}
                      />
                      <span className={classes.FormHelper}>Format: ["avainsana1", "avainsana2"]</span>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>YTJ TOL-toimialakoodit (JSON-lista)</label>
                      <textarea
                        placeholder='["43210"]'
                        value={kb.tolCodesJson || ""}
                        onChange={(e) => handleFieldChange("tolCodesJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                      <span className={classes.FormHelper}>Esimerkki: ["43210", "43220"] sähköasennukselle ja lvi-alalle.</span>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>YTJ TOL-koodien selvennykset (JSON-objekti)</label>
                      <textarea
                        placeholder='{"43210": "Sähköasennus"}'
                        value={kb.tolCodeNamesJson || ""}
                        onChange={(e) => handleFieldChange("tolCodeNamesJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                      <span className={classes.FormHelper}>Format: {"{"}"koodi": "selitys"{"}"}</span>
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Aluerajoitukset (JSON-lista)</label>
                      <input
                        type="text"
                        placeholder='Esim. ["Uusimaa", "Pirkanmaa"]'
                        value={kb.regionLimitationsJson || ""}
                        onChange={(e) => handleFieldChange("regionLimitationsJson", e.target.value)}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Arvioitu tarjoajamäärä Suomessa</label>
                      <input
                        type="number"
                        placeholder="Esim. 1200"
                        value={kb.estimatedProviderCount || ""}
                        onChange={(e) => handleFieldChange("estimatedProviderCount", e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 2: SERVICE */}
              {activeSection === "service" && (
                <div className={classes.FormTabPane}>
                  <h3>Palvelukuvaus ja hintarakenteet</h3>
                  <div className={classes.FormGrid}>
                    <div className={classes.FormGroupFull}>
                      <label>Palvelukuvaus (Yleinen seloste)</label>
                      <textarea
                        placeholder="Mitä tämä palvelu kattaa ja miksi asiakkaat tilaavat sitä..."
                        value={kb.serviceDescription || ""}
                        onChange={(e) => handleFieldChange("serviceDescription", e.target.value)}
                        rows={4}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Mitä palveluun tyypillisesti kuuluu? (JSON-lista)</label>
                      <textarea
                        placeholder='["Sähkökaapelin veto", "Turvakytkimen asennus", "Laitteen testaus"]'
                        value={kb.typicalIncludesJson || ""}
                        onChange={(e) => handleFieldChange("typicalIncludesJson", e.target.value)}
                        rows={3}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Mitä rajattu pois? / Lisämaksulliset (JSON-lista)</label>
                      <textarea
                        placeholder='["Maakaapelointi kaivutöineen", "Keskuksen suurentaminen"]'
                        value={kb.typicalExcludesJson || ""}
                        onChange={(e) => handleFieldChange("typicalExcludesJson", e.target.value)}
                        rows={3}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Toimitustapa / Delivery Type</label>
                      <input
                        type="text"
                        placeholder="Esim. Avaimet käteen -asennus"
                        value={kb.deliveryType || ""}
                        onChange={(e) => handleFieldChange("deliveryType", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Hinnoittelumalli (Pricing Model)</label>
                      <input
                        type="text"
                        placeholder="Esim. Kiinteä hinta tai tuntityö"
                        value={kb.pricingModel || ""}
                        onChange={(e) => handleFieldChange("pricingModel", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Keskimääräinen tilauksen arvo (€)</label>
                      <input
                        type="number"
                        placeholder="Esim. 1500"
                        value={kb.averageOrderValue || ""}
                        onChange={(e) => handleFieldChange("averageOrderValue", e.target.value ? parseFloat(e.target.value) : null)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Hintahaitari (€)</label>
                      <div className={classes.FlexRow}>
                        <input
                          type="number"
                          placeholder="Min (esim 800)"
                          value={kb.priceRangeLow || ""}
                          onChange={(e) => handleFieldChange("priceRangeLow", e.target.value ? parseFloat(e.target.value) : null)}
                        />
                        <span className={classes.FormDash}>—</span>
                        <input
                          type="number"
                          placeholder="Max (esim 2500)"
                          value={kb.priceRangeHigh || ""}
                          onChange={(e) => handleFieldChange("priceRangeHigh", e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      </div>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Sesonkivaihtelut ja huomautukset</label>
                      <input
                        type="text"
                        placeholder="Esim. Kysyntäpiikki keväällä ja syksyllä ennen lumia."
                        value={kb.seasonalityNotes || ""}
                        onChange={(e) => handleFieldChange("seasonalityNotes", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 3: EVALUATION */}
              {activeSection === "evaluation" && (
                <div className={classes.FormTabPane}>
                  <h3>AI-evaluointiasetukset ja pätevyydet</h3>
                  <div className={classes.FormGrid}>
                    <div className={classes.FormGroupFull}>
                      <label>Kultaiset AI-arviointikriteerit (Evaluation Criteria)</label>
                      <textarea
                        placeholder="Määrittele tarkat kriteerit sille, miten Vertex AI arvostelee ehdokkaan sivuston/profiilin. Esim. 'Yrityksen on tarjottava sähköasennuksia ja asennettava seinälatureita. A-arvosana jos suoria referenssejä...'"
                        value={kb.evaluationCriteria || ""}
                        onChange={(e) => handleFieldChange("evaluationCriteria", e.target.value)}
                        rows={6}
                      />
                      <span className={classes.FormHelper}>Arviointimoottori lukee tämän jokaisen haravoidun yrityksen kohdalla.</span>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Poissulkevat avainsanat (JSON-lista)</label>
                      <textarea
                        placeholder='["tukkumyynti", "verkkokauppa", "vain teollisuus"]'
                        value={kb.excludeKeywordsJson || ""}
                        onChange={(e) => handleFieldChange("excludeKeywordsJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                      <span className={classes.FormHelper}>Sivustot, joilla esiintyy näitä sanoja, saavat automaattisesti matalamman arvosanan.</span>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Pakolliset sertifikaatit / pätevyydet (JSON-lista)</label>
                      <textarea
                        placeholder='["Sähköpätevyys 2", "Tukes-rekisteröinti"]'
                        value={kb.requiredCertificationsJson || ""}
                        onChange={(e) => handleFieldChange("requiredCertificationsJson", e.target.value)}
                        rows={3}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Valvovat / sertifioivat elimet (JSON-lista)</label>
                      <textarea
                        placeholder='["Tukes", "Sähkö- ja teleurakoitsijaliitto STUL"]'
                        value={kb.certificationBodiesJson || ""}
                        onChange={(e) => handleFieldChange("certificationBodiesJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 4: COMPLIANCE */}
              {activeSection === "compliance" && (
                <div className={classes.FormTabPane}>
                  <h3>Lainsäädäntö, luvat ja verotus</h3>
                  <div className={classes.FormGrid}>
                    <div className={classes.FormGroupFull}>
                      <label>Lainsäädännöllinen perusta (Legislative Basis)</label>
                      <textarea
                        placeholder="Esim. Sähköturvallisuuslaki 1135/2016 edellyttää, että sähkölaitteistoja asentaa vain ammattilainen..."
                        value={kb.legislativeBasis || ""}
                        onChange={(e) => handleFieldChange("legislativeBasis", e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Vaadittavat toimiluvan tai rekisteröinnit (JSON-lista)</label>
                      <textarea
                        placeholder='["Sähköurakoitsijarekisteri", "Toiminnanharjoittajan ilmoitus Tukesille"]'
                        value={kb.requiredPermitsJson || ""}
                        onChange={(e) => handleFieldChange("requiredPermitsJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Vaadittavat vastuuvakuutukset</label>
                      <input
                        type="text"
                        placeholder="Esim. Toiminnan vastuuvakuutus vähintään 500 000 €"
                        value={kb.requiredInsurance || ""}
                        onChange={(e) => handleFieldChange("requiredInsurance", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Valvovat viranomaiset (JSON-lista)</label>
                      <input
                        type="text"
                        placeholder='["Tukes"]'
                        value={kb.regulatoryBodiesJson || ""}
                        onChange={(e) => handleFieldChange("regulatoryBodiesJson", e.target.value)}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Kotitalousvähennyskelpoinen?</label>
                      <div className={classes.CheckboxLabel}>
                        <input
                          type="checkbox"
                          checked={!!kb.taxDeductionEligible}
                          onChange={(e) => handleFieldChange("taxDeductionEligible", e.target.checked)}
                        />
                        <span>Kyllä, palvelu on kotitalousvähennyskelpoinen työn osuudesta.</span>
                      </div>
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Vähennyksen lähde-URL (todiste)</label>
                      <input
                        type="text"
                        placeholder="Esim. https://www.vero.fi/henkiloasiakkaat/verokortti-ja-verokortti/kotitalousvahennys/"
                        value={kb.taxDeductionSourceUrl || ""}
                        onChange={(e) => handleFieldChange("taxDeductionSourceUrl", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Kotitalousvähennyksen tarkemmat tiedot</label>
                      <textarea
                        placeholder="Esim. Kotitalousvähennys on 40% ennakkoperintärekisteriin kuuluvan yrityksen laskuttaman työn osuudesta..."
                        value={kb.taxDeductionDetails || ""}
                        onChange={(e) => handleFieldChange("taxDeductionDetails", e.target.value)}
                        rows={2}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Hankix-vastuuvapauslausekkeet ja huomiot</label>
                      <textarea
                        placeholder="Mitä tilaajan tai Hankixin on syytä varmistaa tarjouksentekohetkellä..."
                        value={kb.hankixLiabilityNotes || ""}
                        onChange={(e) => handleFieldChange("hankixLiabilityNotes", e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 5: MARKETING */}
              {activeSection === "marketing" && (
                <div className={classes.FormTabPane}>
                  <h3>Kohderyhmät ja asiakasymmärrys</h3>
                  <div className={classes.FormGrid}>
                    <div className={classes.FormGroupFull}>
                      <label>Kohderyhmät (JSON-lista)</label>
                      <textarea
                        placeholder='["Omakotitalon omistajat", "Taloyhtiöt", "Yrityskiinteistöt"]'
                        value={kb.targetGroupsJson || ""}
                        onChange={(e) => handleFieldChange("targetGroupsJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Ostopäätöksen laukaisimet / Primary Triggers (JSON-lista)</label>
                      <textarea
                        placeholder='["Uuden sähköauton hankinta", "Halu ladata autoa edullisesti yöllä", "Kodin latausturvallisuuden parantaminen"]'
                        value={kb.primaryTriggersJson || ""}
                        onChange={(e) => handleFieldChange("primaryTriggersJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Asiakkaan pelot ja epävarmuudet / Customer Fears (JSON-lista)</label>
                      <textarea
                        placeholder='["Sähköverkon ylikuormittuminen latatessa", "Yllätyskulut asennuksessa", "Latauslaitteen yhteensopivuusongelmat"]'
                        value={kb.customerFearsJson || ""}
                        onChange={(e) => handleFieldChange("customerFearsJson", e.target.value)}
                        rows={2}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Asiakkaan ostokriteerit (Buying Criteria)</label>
                      <textarea
                        placeholder="Esim. 1. Turvallisuus ja luotettavuus, 2. Helppokäyttöisyys, 3. Hinta-laatusuhde, 4. Nopea toimitusaika..."
                        value={kb.buyingCriteria || ""}
                        onChange={(e) => handleFieldChange("buyingCriteria", e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Usein kysytyt kysymykset / FAQs (JSON-lista / objekteja)</label>
                      <textarea
                        placeholder='[{"q": "Kauan asennus kestää?", "a": "Yleensä 2-4 tuntia."}]'
                        value={kb.faqsJson || ""}
                        onChange={(e) => handleFieldChange("faqsJson", e.target.value)}
                        rows={4}
                        className={classes.CodeTextArea}
                      />
                      <span className={classes.FormHelper}>Format: [{"{"}"q": "kysymys", "a": "vastaus"{"}"}]</span>
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Kotitalousvähennys Infolaatikko (Kotisivuille)</label>
                      <textarea
                        placeholder="Esim. Muista hyödyntää kotitalousvähennys! Säästät työn osuudesta satoja euroja..."
                        value={kb.kotitalousvahennysInfo || ""}
                        onChange={(e) => handleFieldChange("kotitalousvahennysInfo", e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 6: BRANDING */}
              {activeSection === "branding" && (
                <div className={classes.FormTabPane}>
                  <h3>Brändi-ilme, SEO & Golden System Prompt</h3>
                  <div className={classes.FormGrid}>
                    <div className={classes.FormGroup}>
                      <label>Brändiväri (Hex-koodi / Brand Color)</label>
                      <div className={classes.FlexRow}>
                        <input
                          type="color"
                          value={kb.brandColor || "#2563eb"}
                          onChange={(e) => handleFieldChange("brandColor", e.target.value)}
                          className={classes.ColorPicker}
                        />
                        <input
                          type="text"
                          placeholder="#2563eb"
                          value={kb.brandColor || ""}
                          onChange={(e) => handleFieldChange("brandColor", e.target.value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Sivuston kooditunniste (Site Config Code)</label>
                      <input
                        type="text"
                        placeholder="Esim. lataus-site"
                        value={kb.siteConfigCode || ""}
                        onChange={(e) => handleFieldChange("siteConfigCode", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Hero Headline (Laskeutumissivun otsikko)</label>
                      <input
                        type="text"
                        placeholder="Esim. Turvallinen ja nopea sähköauton latausasema kotiisi avaimet käteen -asennuksella."
                        value={kb.heroHeadline || ""}
                        onChange={(e) => handleFieldChange("heroHeadline", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroup}>
                      <label>Hero Copy (Laskeutumissivun esittelyteksti)</label>
                      <input
                        type="text"
                        placeholder="Esim. Pyydä tarjoukset alueesi valtuutetuilta sähköasentajilta vaivattomasti yhdellä lomakkeella."
                        value={kb.heroCopy || ""}
                        onChange={(e) => handleFieldChange("heroCopy", e.target.value)}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Kuluttajasivuston Info Grid Blocks (JSON-rakenne)</label>
                      <textarea
                        placeholder='[{"title": "Turvallinen asennus", "text": "Kaikki asennukset Tukes-rekisteröidyiltä yrityksiltä."}]'
                        value={kb.infoGridBlocksJson || ""}
                        onChange={(e) => handleFieldChange("infoGridBlocksJson", e.target.value)}
                        rows={3}
                        className={classes.CodeTextArea}
                      />
                    </div>

                    <div className={classes.FormGroupFull}>
                      <label>Kultainen Injektioprompt (Golden Prompt Fragment)</label>
                      <textarea
                        placeholder="Injektoidaan Vertex AI -arviointiin ja tarjouksentekoon järjestelmäprompiksi. Kirjoita tähän äärimmäisen tarkat ohjeet..."
                        value={kb.promptFragment || ""}
                        onChange={(e) => handleFieldChange("promptFragment", e.target.value)}
                        rows={5}
                      />
                      <span className={classes.FormHelper}>Tämä sääntöjoukko ohjaa kaikkia tämän vertikaalin tekoälytoimintoja.</span>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* SAVE FOOTER */}
            <div className={classes.KbFooter}>
              <div className={classes.KbMetaInfo}>
                {kb.updated && (
                  <span>
                    Viimeksi päivitetty: {new Date(kb.updated).toLocaleString("fi-FI")}
                  </span>
                )}
              </div>
              <button type="submit" className={classes.PrimaryBtn} disabled={saving}>
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Tallennetaan...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} /> Tallenna muutokset
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* List controls */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
            background: "white",
            padding: "16px 20px",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}>
            <div style={{ display: "flex", gap: "12px", flex: 1, maxWidth: "500px" }}>
              <div style={{ position: "relative", flex: 1 }}>
                <input
                  type="text"
                  placeholder="Hae tietoalustoja nimen tai tunnisteen mukaan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px 10px 36px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "0.88rem",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                  }} 
                />
              </div>
            </div>
            
            <button 
              className={classes.PrimaryBtn} 
              onClick={() => {
                setShowCreateModal(true);
                setCreateType("linked");
                setSelectedServiceId("");
                setNewServiceToken("");
                setNewVerticalName("");
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Luo uusi tietoalusta
            </button>
          </div>

          {/* Table / List */}
          {loadingList ? (
            <div className={classes.Spinner}>
              <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Ladataan tietoalustoja...
            </div>
          ) : filteredKbList.length > 0 ? (
            <div style={{
              background: "white",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
              overflow: "hidden",
            }}>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                fontSize: "0.88rem",
              }}>
                <thead>
                  <tr style={{
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#475569",
                    fontWeight: 600,
                  }}>
                    <th style={{ padding: "14px 20px" }}>Vertikaalin nimi (Vertical Name)</th>
                    <th style={{ padding: "14px 20px" }}>Tunniste (Service Token)</th>
                    <th style={{ padding: "14px 20px" }}>Kytketty palvelu (Service)</th>
                    <th style={{ padding: "14px 20px" }}>Julkaisutila</th>
                    <th style={{ padding: "14px 20px" }}>Viimeksi päivitetty</th>
                    <th style={{ padding: "14px 20px", textAlign: "right" }}>Toiminnot</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKbList.map((k) => {
                    const linkedService = services.find(s => String(s.id) === String(k.serviceId));
                    return (
                      <tr 
                        key={k.id || k.serviceToken} 
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background-color 0.15s",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        onClick={() => handleLoadKB(k.serviceToken)}
                      >
                        <td style={{ padding: "16px 20px", fontWeight: 600, color: "#0f172a" }}>
                          {k.verticalName || "Nimetön vertikaali"}
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <span className={classes.CodeText}>{k.serviceToken}</span>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          {k.serviceId ? (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: "#eff6ff",
                              color: "#2563eb",
                              padding: "4px 10px",
                              borderRadius: "100px",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                            }}>
                              <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#3b82f6" }}></span>
                              {linkedService ? linkedService.name : `Palvelu (ID: ${k.serviceId})`}
                            </span>
                          ) : (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: "#f1f5f9",
                              color: "#64748b",
                              padding: "4px 10px",
                              borderRadius: "100px",
                              fontSize: "0.78rem",
                              fontWeight: 500,
                            }}>
                              <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#94a3b8" }}></span>
                              Itsenäinen (ei kytkentää)
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <span className={`${classes.StatusBadge} ${
                            k.launchStatus === "LAUNCHED" || k.launchStatus === "ACTIVE" 
                              ? classes.StatusCompleted 
                              : classes.StatusPending
                          }`}>
                            {k.launchStatus || "DRAFT"}
                          </span>
                        </td>
                        <td style={{ padding: "16px 20px", color: "#64748b", fontSize: "0.82rem" }}>
                          {k.updated ? new Date(k.updated).toLocaleString("fi-FI") : "-"}
                        </td>
                        <td style={{ padding: "16px 20px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <button 
                            className={classes.SecondaryBtnSmall}
                            onClick={() => handleLoadKB(k.serviceToken)}
                          >
                            <FontAwesomeIcon icon={faBookOpen} /> Avaa muokkain
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={classes.EmptyState} style={{ background: "white", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "60px 20px" }}>
              <FontAwesomeIcon icon={faBookOpen} className={classes.EmptyIcon} style={{ fontSize: "2.5rem", marginBottom: "12px" }} />
              <h3>Ei tietoalustoja</h3>
              <p style={{ maxWidth: "400px", margin: "0 auto 20px auto" }}>
                {searchTerm ? "Hakuehdoillasi ei löytynyt yhtään tietoalustaa. Kokeile toista hakusanaa." : "Järjestelmään ei ole vielä luotu yhtään tietoalustaa. Aloita luomalla ensimmäinen!"}
              </p>
              {!searchTerm && (
                <button 
                  className={classes.PrimaryBtn} 
                  onClick={() => {
                    setShowCreateModal(true);
                    setCreateType("linked");
                    setSelectedServiceId("");
                    setNewServiceToken("");
                    setNewVerticalName("");
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Luo ensimmäinen tietoalusta
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* CREATION MODAL */}
      {showCreateModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(15, 23, 42, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(4px)",
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            width: "100%",
            maxWidth: "500px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* Modal Header */}
            <div style={{
              padding: "16px 24px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}>Luo uusi tietoalusta (Knowledge Base)</h3>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                  fontSize: "1.1rem",
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              {createError && <div className={classes.ErrorMsg}>{createError}</div>}

              {/* Type selector */}
              <div className={classes.FormGroup}>
                <label>Miten tietoalusta luodaan?</label>
                <div style={{ display: "flex", gap: "16px", marginTop: "4px" }}>
                  <label className={classes.CheckboxLabel} style={{ fontWeight: 500 }}>
                    <input 
                      type="radio" 
                      name="createType" 
                      checked={createType === "linked"} 
                      onChange={() => {
                        setCreateType("linked");
                        setNewServiceToken("");
                        setNewVerticalName("");
                      }} 
                    />
                    <span>Kytke olemassa olevaan palveluun (suositus)</span>
                  </label>
                  <label className={classes.CheckboxLabel} style={{ fontWeight: 500 }}>
                    <input 
                      type="radio" 
                      name="createType" 
                      checked={createType === "independent"} 
                      onChange={() => {
                        setCreateType("independent");
                        setSelectedServiceId("");
                      }} 
                    />
                    <span>Luo itsenäinen tietoalusta</span>
                  </label>
                </div>
              </div>

              {createType === "linked" ? (
                <>
                  <div className={classes.FormGroup}>
                    <label>Valitse palvelu <span className={classes.Required}>*</span></label>
                    <select
                      value={selectedServiceId}
                      onChange={(e) => {
                        const sId = e.target.value;
                        setSelectedServiceId(sId);
                        const selectedService = services.find(s => String(s.id) === String(sId));
                        if (selectedService) {
                          setNewServiceToken(selectedService.serviceToken || "");
                          setNewVerticalName(selectedService.name || "");
                        } else {
                          setNewServiceToken("");
                          setNewVerticalName("");
                        }
                      }}
                      style={{
                        padding: "10px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "0.88rem",
                        backgroundColor: "white",
                        outline: "none",
                      }}
                    >
                      <option value="">-- Valitse palvelu --</option>
                      {services
                        .filter(s => !kbList.some(k => String(k.serviceId) === String(s.id))) // hide already created ones
                        .map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.serviceToken})</option>
                        ))
                      }
                    </select>
                    <span className={classes.FormHelper}>Listassa näytetään vain palvelut, joille ei ole vielä luotu tietoalustaa.</span>
                  </div>

                  {newServiceToken && (
                    <>
                      <div className={classes.FormGroup}>
                        <label>Palvelutunniste (Service Token)</label>
                        <input 
                          type="text" 
                          value={newServiceToken} 
                          disabled 
                          style={{ backgroundColor: "#f1f5f9", cursor: "not-allowed" }}
                        />
                      </div>

                      <div className={classes.FormGroup}>
                        <label>Vertikaalin nimi (Vertical Name) <span className={classes.Required}>*</span></label>
                        <input 
                          type="text" 
                          value={newVerticalName} 
                          onChange={(e) => setNewVerticalName(e.target.value)} 
                          placeholder="Esim. Aurinkosähköjärjestelmät"
                        />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className={classes.FormGroup}>
                    <label>Palvelutunniste (Service Token) <span className={classes.Required}>*</span></label>
                    <input 
                      type="text" 
                      value={newServiceToken} 
                      onChange={(e) => setNewServiceToken(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} 
                      placeholder="Esim. putkimies tai auton-huolto"
                    />
                    <span className={classes.FormHelper}>Pienet kirjaimet, numerot, viivat ja alaviivat sallittu.</span>
                  </div>

                  <div className={classes.FormGroup}>
                    <label>Vertikaalin nimi (Vertical Name) <span className={classes.Required}>*</span></label>
                    <input 
                      type="text" 
                      value={newVerticalName} 
                      onChange={(e) => setNewVerticalName(e.target.value)} 
                      placeholder="Esim. Putkityöt ja putkimiehet"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: "16px 24px",
              backgroundColor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}>
              <button 
                className={classes.SecondaryBtn} 
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                disabled={createLoading}
                style={{ padding: "8px 16px", fontSize: "0.85rem" }}
              >
                Peruuta
              </button>
              <button 
                className={classes.PrimaryBtn}
                onClick={handleCreateKB}
                disabled={createLoading || !newServiceToken.trim() || !newVerticalName.trim() || (createType === "linked" && !selectedServiceId)}
                style={{ padding: "8px 16px", fontSize: "0.85rem" }}
              >
                {createLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className={classes.SpinnerIcon} /> Luodaan...
                  </>
                ) : (
                  <>Luo tietoalusta</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default KnowledgeBaseTab;
